import React from 'react';
import ListView from '../listview/ListView';
import FormatSettingsContext from '../components/FormatSettingsContext';
import useAutoRefresh from '../hooks/useAutoRefresh';
import useListViewBookings from '../hooks/useListViewBookings';
import useListViewFilterOptions from '../hooks/useListViewFilterOptions';
import useViewSettings from '../hooks/useViewSettings';
import type { ListViewConfiguration } from '../listview/types';

/* Renders one configured view. It composes what the view needs; the work
 * itself lives in four hooks:
 *
 *   useListViewFilterOptions  what the filter panel offers
 *   useViewSettings           the client-side view state, restored from and
 *                             saved to the signed-in user's stored settings
 *   useListViewBookings       the bookings of the selected day, with the
 *                             snapshot baseline and the refresh markers
 *   useAutoRefresh            the refresh interval and the background-tab
 *                             catch-up
 **/

interface ListViewContainerProps {
  host: string;
  configuration: ListViewConfiguration;
}

const ListViewContainer = ({ host, configuration }: ListViewContainerProps) => {
  const { dateFormatSetting, timeFormatSetting } =
    React.useContext(FormatSettingsContext);

  const {
    resourceGroups,
    resourceRoles,
    eventStatuses,
    eventProfiles,
    resourceCustomDataFields,
    resourceBookingCustomDataFields
  } = useListViewFilterOptions(host);

  const settings = useViewSettings({ host, viewID: configuration.id });

  const bookings = useListViewBookings({
    host,
    viewID: configuration.id,
    configuration,
    date: settings.date,
    activeFilters: settings.activeFilters,
    settingsSettled: settings.settingsSettled
  });

  const bookingsLoading = !bookings.bookingsFetched && !bookings.errorCode;

  useAutoRefresh({
    intervalMinutes: settings.refreshIntervalMinutes,
    lastRefreshedAt: bookings.lastRefreshedAt,
    enabled: bookings.bookingsFetched && !bookings.errorCode,
    onRefresh: bookings.refresh
  });

  return (
    <ListView
      bookings={bookings.bookings}
      bookingsFetched={bookings.bookingsFetched}
      bookingsFailed={bookings.errorCode !== false}
      resourceCustomDataFields={resourceCustomDataFields}
      resourceBookingCustomDataFields={resourceBookingCustomDataFields}
      resourceGroups={resourceGroups}
      resourceRoles={resourceRoles}
      eventStatuses={eventStatuses}
      eventProfiles={eventProfiles}
      configuration={configuration}
      viewMode={settings.viewMode}
      onViewModeChange={settings.setViewMode}
      date={settings.date}
      onDateChange={settings.setDate}
      activeFilters={settings.activeFilters}
      onAddFilter={settings.addFilter}
      onUpdateFilter={settings.updateFilter}
      onRemoveFilter={settings.removeFilter}
      showChildren={settings.showChildren}
      onShowChildrenChange={settings.setShowChildren}
      hidePastBookings={settings.hidePastBookingsOnToday}
      onHidePastBookingsChange={settings.setHidePastBookingsOnToday}
      dateFormatSetting={dateFormatSetting}
      timeFormatSetting={timeFormatSetting}
      diff={bookings.diff}
      refreshControl={{
        intervalMinutes: settings.refreshIntervalMinutes,
        /* The initial load reads as a refresh in progress too, so the
           refresh controls describe the bookings region below them */
        isRefreshing: bookings.isRefreshing || bookingsLoading,
        lastRefreshedAt: bookings.lastRefreshedAt,
        failed: bookings.refreshFailed,
        onIntervalChange: settings.setRefreshInterval,
        onRefreshNow: bookings.refresh
      }}
    />
  );
};

export default ListViewContainer;
